// ============================================================================
// AdminSubscribersPanel — manage email-subscription list + send blasts. (#9)
// ============================================================================
// Sidebar entry: #admin/subscribers. UIF only.
//
// Three KPIs across the top (confirmed, pending, unsubscribed) + a filter
// dropdown + a table with per-row archive, plus a "Send to all confirmed"
// blast composer (reuses the EmailBlastModal pattern but built locally so
// it doesn't need to know about event IDs).
// ============================================================================

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, AlertCircle, Archive, Send, RefreshCw, Download, Mail } from 'lucide-react';
import { api, type Subscriber, type SubscriberStatus } from '../api';

interface Props {
  token: string;
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

export function AdminSubscribersPanel({ token }: Props) {
  const [subs, setSubs] = useState<Subscriber[]>([]);
  const [counts, setCounts] = useState<Record<SubscriberStatus, number>>({ pending: 0, confirmed: 0, unsubscribed: 0 });
  const [filter, setFilter] = useState<SubscriberStatus | 'all'>('confirmed');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [blastOpen, setBlastOpen] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());

  const refresh = useCallback(() => {
    setLoading(true);
    setError('');
    api.adminSubscribers(token, filter === 'all' ? undefined : filter)
      .then((d) => {
        setSubs(d.subscribers ?? []);
        const next: Record<SubscriberStatus, number> = { pending: 0, confirmed: 0, unsubscribed: 0 };
        for (const c of d.counts ?? []) next[c.status] = c.count;
        setCounts(next);
      })
      .catch((err) => setError(err?.message || 'Could not load subscribers.'))
      .finally(() => setLoading(false));
  }, [token, filter]);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => { setSelected(new Set()); }, [filter]);

  const allSelectedOnPage = useMemo(
    () => subs.length > 0 && subs.every((s) => selected.has(s.subscriber_id)),
    [subs, selected],
  );

  async function archive(s: Subscriber) {
    if (!confirm(`Remove ${s.email} from the subscriber list? They can re-sign up later if they want.`)) return;
    try {
      await api.archiveSubscriber(token, s.subscriber_id);
      refresh();
    } catch (err: any) {
      setError(err?.message || 'Could not archive.');
    }
  }

  function downloadCsv() {
    if (subs.length === 0) return;
    const headers = ['subscriber_id', 'email', 'full_name', 'status', 'source', 'subscribed_at', 'confirmed_at'];
    const escape = (v: any) => {
      const s = v == null ? '' : String(v);
      if (s.includes(',') || s.includes('"') || s.includes('\n')) {
        return `"${s.replace(/"/g, '""')}"`;
      }
      return s;
    };
    const lines = [
      headers.join(','),
      ...subs.map((s) => headers.map((h) => escape((s as any)[h])).join(',')),
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `uif-subscribers-${filter}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Subscribers</h1>
          <p className="text-sm text-gray-600 mt-0.5">
            People who opted in to "new workshop" notifications from the public homepage. Double-opt-in: only confirmed subscribers receive blasts.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setBlastOpen(true)}
          className="inline-flex items-center gap-1.5 rounded-md bg-uif-green text-white font-semibold px-3 py-2 text-sm hover:bg-uif-green-dark"
        >
          <Send className="h-4 w-4" />
          Send a blast
        </button>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Kpi label="Confirmed" value={counts.confirmed} tone="ok" />
        <Kpi label="Pending" value={counts.pending} tone="warn" />
        <Kpi label="Unsubscribed" value={counts.unsubscribed} tone="off" />
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-3 flex flex-wrap items-end gap-3">
        <label className="block">
          <span className="block text-xs font-medium text-gray-700 mb-1">Filter</span>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as any)}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none"
          >
            <option value="confirmed">Confirmed ({counts.confirmed})</option>
            <option value="pending">Pending ({counts.pending})</option>
            <option value="unsubscribed">Unsubscribed ({counts.unsubscribed})</option>
            <option value="all">All</option>
          </select>
        </label>
        <div className="flex-1" />
        <button
          type="button"
          onClick={downloadCsv}
          disabled={subs.length === 0}
          className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          <Download className="h-4 w-4" />
          CSV
        </button>
        <button
          type="button"
          onClick={refresh}
          disabled={loading}
          className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw className={'h-4 w-4 ' + (loading ? 'animate-spin' : '')} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2 text-sm flex items-start gap-2">
          <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <div>{error}</div>
        </div>
      )}

      {selected.size > 0 && (
        <div className="bg-uif-green/5 border border-uif-green/30 rounded-md px-3 py-2 text-sm flex items-center gap-3">
          <span className="font-semibold text-uif-green-dark">{selected.size} selected</span>
          <button
            type="button"
            onClick={() => setBlastOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-md bg-uif-green text-white font-semibold px-3 py-1.5 text-xs hover:bg-uif-green-dark"
          >
            <Send className="h-3.5 w-3.5" />
            Email selected ({selected.size})
          </button>
          <button
            type="button"
            onClick={() => setSelected(new Set())}
            className="text-xs text-uif-green-dark hover:underline"
          >
            Clear selection
          </button>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-gray-500 uppercase tracking-wider border-b border-gray-200">
            <tr>
              <th className="px-3 py-2">
                <input
                  type="checkbox"
                  checked={allSelectedOnPage}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelected(new Set(subs.map((s) => s.subscriber_id)));
                    } else {
                      setSelected(new Set());
                    }
                  }}
                  aria-label="Select all"
                />
              </th>
              <th className="px-3 py-2">Email</th>
              <th className="px-3 py-2">Name</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Subscribed</th>
              <th className="px-3 py-2">Source</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading && (
              <tr><td colSpan={7} className="px-3 py-4 text-gray-500 text-center"><Loader2 className="inline h-4 w-4 animate-spin mr-1" />Loading…</td></tr>
            )}
            {!loading && subs.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-6 text-gray-500 italic text-center">No subscribers in this view.</td></tr>
            )}
            {subs.map((s) => (
              <tr key={s.subscriber_id} className="hover:bg-gray-50">
                <td className="px-3 py-2">
                  <input
                    type="checkbox"
                    checked={selected.has(s.subscriber_id)}
                    onChange={(e) => {
                      const next = new Set(selected);
                      if (e.target.checked) next.add(s.subscriber_id);
                      else next.delete(s.subscriber_id);
                      setSelected(next);
                    }}
                    aria-label={`Select ${s.email}`}
                  />
                </td>
                <td className="px-3 py-2 font-medium text-gray-900">{s.email}</td>
                <td className="px-3 py-2 text-gray-700">{s.full_name || <span className="italic text-gray-400">—</span>}</td>
                <td className="px-3 py-2">
                  <StatusPill status={s.status} />
                </td>
                <td className="px-3 py-2 text-gray-700">{fmtDate(s.subscribed_at)}</td>
                <td className="px-3 py-2 text-xs text-gray-500">{s.source || '—'}</td>
                <td className="px-3 py-2 text-right">
                  <button
                    type="button"
                    onClick={() => archive(s)}
                    className="p-1.5 rounded hover:bg-gray-100 text-gray-600"
                    title="Remove from list"
                    aria-label={`Archive ${s.email}`}
                  >
                    <Archive className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {blastOpen && (
        <BlastComposer
          token={token}
          confirmedCount={counts.confirmed}
          selectedIds={selected.size > 0 ? Array.from(selected) : null}
          onClose={() => setBlastOpen(false)}
          onSent={() => { setBlastOpen(false); setSelected(new Set()); refresh(); }}
        />
      )}
    </div>
  );
}

function Kpi({ label, value, tone }: { label: string; value: number; tone: 'ok' | 'warn' | 'off' }) {
  const ring =
    tone === 'ok' ? 'border-emerald-100 bg-emerald-50/50'
    : tone === 'warn' ? 'border-amber-100 bg-amber-50/50'
    : 'border-gray-200 bg-gray-50';
  return (
    <div className={'rounded-lg border p-3 ' + ring}>
      <div className="text-[11px] uppercase tracking-wider text-gray-500">{label}</div>
      <div className="text-2xl font-bold text-gray-900 mt-1">{value.toLocaleString()}</div>
    </div>
  );
}

function StatusPill({ status }: { status: SubscriberStatus }) {
  const cls =
    status === 'confirmed' ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
    : status === 'pending' ? 'bg-amber-50 text-amber-800 border-amber-200'
    : 'bg-gray-100 text-gray-600 border-gray-200';
  return <span className={'inline-block text-[11px] uppercase tracking-wider px-2 py-0.5 rounded-full border ' + cls}>{status}</span>;
}

// --- Inline blast composer (kept local so Subscribers panel doesn't depend on
//     the event-specific EmailBlastModal). ---

function BlastComposer({
  token, confirmedCount, selectedIds, onClose, onSent,
}: {
  token: string;
  confirmedCount: number;
  selectedIds: number[] | null;
  onClose: () => void;
  onSent: () => void;
}) {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('Hi there,\n\nWe just announced a new workshop you might be interested in: [Workshop title](https://events.upinfarms.com/#e/SLUG-HERE).\n\n— Up in Farms');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<{ sent: number; failed: number } | null>(null);

  const recipientLabel = selectedIds
    ? `${selectedIds.length} selected subscriber${selectedIds.length === 1 ? '' : 's'}`
    : `all ${confirmedCount} confirmed subscriber${confirmedCount === 1 ? '' : 's'}`;

  async function send(e: React.FormEvent) {
    e.preventDefault();
    if (sending) return;
    if (!confirm(`Send "${subject}" to ${recipientLabel}? This cannot be un-sent.`)) return;
    setSending(true);
    setError('');
    try {
      const r = await api.sendSubscriberBlast(token, {
        subject: subject.trim(),
        body_md: body.trim(),
        recipient_filter: 'confirmed',
        subscriber_ids: selectedIds ?? undefined,
      });
      setResult({ sent: r.sent, failed: r.failed });
      setTimeout(onSent, 1500);
    } catch (err: any) {
      setError(err?.message || 'Could not send.');
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="border-b border-gray-200 px-4 py-3 flex items-center justify-between">
          <h2 className="text-base font-semibold text-uif-green-dark flex items-center gap-2">
            <Mail className="h-4 w-4" />
            Subscriber blast
          </h2>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-700">✕</button>
        </div>
        <form onSubmit={send} className="p-4 space-y-3">
          <div className="bg-uif-green/5 border border-uif-green/30 rounded-md px-3 py-2 text-sm">
            Sending to <strong>{recipientLabel}</strong>. Pending and unsubscribed addresses are skipped.
          </div>
          <label className="block">
            <span className="block text-xs font-medium text-gray-700 mb-1">Subject</span>
            <input
              type="text"
              required
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              maxLength={255}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none"
            />
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-gray-700 mb-1">Body (markdown)</span>
            <textarea
              required
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={12}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-mono focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none"
            />
            <p className="text-[11px] text-gray-500 mt-1">
              An unsubscribe footer is auto-appended unless you include the literal token <code>{'{{UNSUBSCRIBE_URL}}'}</code> somewhere in the body.
            </p>
          </label>
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2 text-sm">{error}</div>
          )}
          {result && (
            <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-md px-3 py-2 text-sm">
              Sent {result.sent}, failed {result.failed}.
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={sending}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={sending || !subject.trim() || !body.trim()}
              className="inline-flex items-center gap-1.5 rounded-md bg-uif-green text-white font-semibold px-4 py-2 text-sm hover:bg-uif-green-dark disabled:bg-gray-300"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Send
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
